import {
    CaseDocumentSummary,
    CaseReport,
} from '../../../../services/reportService';

export const CASE_CONTEXT_SCHEMA_KEY = 'case-context';
export const INITIAL_EVALUATION_SCHEMA_KEY = 'initial-evaluation';
export const RISK_BENEFIT_SCHEMA_KEY = 'risk-benefit-analysis';
export const POSITIONING_IMMOBILIZATION_SCHEMA_KEY =
    'positioning-immobilization';
export const DEVICE_PREPARATION_SCHEMA_KEY = 'device-preparation';
export const FORM_ACTIONS_SCHEMA_KEY = 'form-actions';

export const CASE_DOCUMENT_SCHEMA_KEYS = [
    CASE_CONTEXT_SCHEMA_KEY,
    INITIAL_EVALUATION_SCHEMA_KEY,
    DEVICE_PREPARATION_SCHEMA_KEY,
    POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
    RISK_BENEFIT_SCHEMA_KEY,
] as const;

export type CaseDocumentSchemaKey = (typeof CASE_DOCUMENT_SCHEMA_KEYS)[number];

export const CASE_CONTEXT_FIELD_IDS = [
    'caseId',
    'exam',
    'clinicalIndication',
    'scannerId',
    'scanDate',
    'authors',
] as const;

export type CaseContextFieldId = (typeof CASE_CONTEXT_FIELD_IDS)[number];

export type WorkspaceDocumentMode = 'view' | 'edit';

export interface CaseContextValues {
    caseId: string;
    exam: string;
    clinicalIndication: string;
    scannerId: string;
    scanDate: string;
    authors: string;
}

const toStringValue = (value: unknown): string => {
    if (typeof value !== 'string') return '';
    return value.trim();
};

export const getCaseContextValuesFromData = (
    data?: Record<string, any> | null,
    fallbackCaseId?: string
): CaseContextValues => {
    const source = data || {};

    return {
        caseId:
            toStringValue(source.caseId) ||
            toStringValue(source.caseToken) ||
            toStringValue(fallbackCaseId),
        exam: toStringValue(source.exam),
        clinicalIndication: toStringValue(source.clinicalIndication),
        scannerId: toStringValue(source.scannerId),
        scanDate:
            toStringValue(source.scanDate) ||
            toStringValue(source.assessmentDate),
        authors: toStringValue(source.authors),
    };
};

export const createRiskBenefitDefaultsFromInitialEvaluation = (
    initialEvaluationDocument?: CaseDocumentSummary | null
) => {
    return createRiskBenefitDefaultsFromInitialEvaluationData(
        initialEvaluationDocument?.data,
        initialEvaluationDocument?.document_id
    );
};

export const createRiskBenefitDefaultsFromInitialEvaluationData = (
    initialEvaluationData?: Record<string, any> | null,
    initialEvaluationDocumentId?: string | null
) => {
    const initialData = initialEvaluationData || {};
    const hasInitialEvaluationData = Object.keys(initialData).length > 0;
    const evidenceReviewed = initialEvaluationDocumentId
        ? [
              {
                  type: 'Tech assessment (76014 note)',
                  referenceDocumentId: initialEvaluationDocumentId,
              },
          ]
        : hasInitialEvaluationData
        ? [
              {
                  type: 'Tech assessment (76014 note)',
              },
          ]
        : [];

    return {
        ...(evidenceReviewed.length ? { evidenceReviewed } : {}),
    };
};

export const buildCaseBaseDefaults = (
    _caseData: CaseReport,
    userName?: string
): Record<string, any> => {
    return {
        techName: userName || '',
    };
};

export const getDocumentBySchemaKey = (
    caseData: CaseReport | null,
    schemaKey: CaseDocumentSchemaKey
): CaseDocumentSummary | null => {
    if (!caseData?.documents?.length) return null;
    return (
        caseData.documents.find(
            (document) => document.schemaKey === schemaKey
        ) || null
    );
};

export const getInitialEvaluationSummary = (
    initialEvaluationDocument?: CaseDocumentSummary | null
) => {
    return getInitialEvaluationSummaryFromData(initialEvaluationDocument?.data);
};

export const getInitialEvaluationFeasibilityValue = (
    initialEvaluationData?: Record<string, any> | null
) => {
    return toStringValue(initialEvaluationData?.feasibility);
};

export const getInitialEvaluationSummaryFromData = (
    initialEvaluationData?: Record<string, any> | null
) => {
    const data = initialEvaluationData || {};
    const implants = Array.isArray(data.implants) ? data.implants : [];
    const implantSummary = implants
        .map(
            (implant: any) =>
                implant?.implantName || implant?._implantModel || ''
        )
        .filter(Boolean)
        .join(', ');

    return {
        feasibility: toStringValue(data.feasibility) || 'unclear',
        implantSummary: implantSummary || 'Not documented',
    };
};

export const getCaseContextValues = (
    caseData: CaseReport | null
): CaseContextValues => {
    const caseContextDocument = getDocumentBySchemaKey(
        caseData,
        CASE_CONTEXT_SCHEMA_KEY
    );
    const caseContextData =
        caseContextDocument && caseContextDocument.data
            ? caseContextDocument.data
            : null;

    return getCaseContextValuesFromData(caseContextData, caseData?.caseId);
};
